import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Chip,
  IconButton,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Menu,
  MenuItem,
  Popover,
  Grid,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Close as CloseIcon,
} from "@mui/icons-material";
import Header from "../components/header";
import RequestView from "./requests/requestView";
import ScheduledView from "./scheduled/scheduledView";
import PastView from "./past/pastView";
import { useSelector } from "react-redux";
import { authenticatedFetch } from "../../network/api";

export default function ModernInbox() {
  const [activeTab, setActiveTab] = useState("requests");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyUpdates, setShowOnlyUpdates] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date(2023, 2)); // March 2023 to match design
  const [requestCount, setRequestCount] = useState(0);
  const [scheduledCount, setScheduledCount] = useState(0);
  const [pastCount, setPastCount] = useState(0);
  const [requestAppointments, setRequestAppointments] = useState([]);
  const [scheduledAppointments, setScheduledAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const lawyerId = useSelector((s) => s.attorneyData.attorneyData?.user_id);

  useEffect(() => {
    fetchAllAppointments();
  }, []);

  const fetchAllAppointments = async () => {
    try {
      setLoading(true);
      // Fetch both types of appointments in parallel
      const [requestsResponse, scheduledResponse] = await Promise.all([
        authenticatedFetch(
          `/api/attorney/webapp/fetchAppointmentRequests?lawyer_id=${lawyerId}`
        ),
        authenticatedFetch(
          `/api/attorney/webapp/fetchConfirmedAppointments?lawyer_id=${lawyerId}`
        ),
      ]);

      if (!requestsResponse.ok || !scheduledResponse.ok) {
        throw new Error("Failed to fetch appointments");
      }

      const [requestsData, scheduledData] = await Promise.all([
        requestsResponse.json(),
        scheduledResponse.json(),
      ]);

      setRequestAppointments(requestsData);
      setScheduledAppointments(scheduledData.upcoming || []);
      setPastAppointments(scheduledData.past || []);
      setRequestCount(requestsData.length);
      setScheduledCount((scheduledData.upcoming || []).length);
      setPastCount((scheduledData.past || []).length);
    } catch (err) {
      setError(err.message);
      setRequestCount(0);
      setScheduledCount(0);
      setPastCount(0);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "requests", label: "Requests", count: requestCount },
    {
      id: "scheduled",
      label: "Scheduled",
      count: scheduledCount,
    },
    { id: "past", label: "Past", count: pastCount },
  ];

  const filterOptions = [
    "Appointment Date",
    "Intake Form",
    "Has Document",
    "AI Summary",
    "Urgent",
  ];

  const timeRangeOptions = [
    "Yesterday",
    "Last 7 days",
    "Last 30 days",
    "Custom Range",
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleCalendarClick = (event) => {
    setCalendarAnchor(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchor(null);
  };

  const toggleFilter = (filter) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filter]: !prev[filter],
    }));
  };

  const clearFilter = (filter) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filter];
      return newFilters;
    });
  };

  const renderActiveView = () => {
    switch (activeTab) {
      case "requests":
        return (
          <RequestView
            searchQuery={searchQuery}
            showOnlyUpdates={showOnlyUpdates}
            selectedFilters={selectedFilters}
            appointments={requestAppointments}
            loading={loading}
            error={error}
            onRefresh={fetchAllAppointments}
          />
        );
      case "scheduled":
        return (
          <ScheduledView
            searchQuery={searchQuery}
            showOnlyUpdates={showOnlyUpdates}
            selectedFilters={selectedFilters}
            appointments={scheduledAppointments}
            loading={loading}
            error={error}
            onRefresh={fetchAllAppointments}
          />
        );
      case "past":
        return (
          <PastView
            searchQuery={searchQuery}
            showOnlyUpdates={showOnlyUpdates}
            selectedFilters={selectedFilters}
            appointments={pastAppointments}
            loading={loading}
            error={error}
            onCount={(n) => setPastCount(n)}
          />
        );
      default:
        return null;
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDateForComparison = new Date();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

    return (
      <Box sx={{ p: 2, width: 280 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCurrentDate(new Date(year, month - 1))}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <Typography variant="subtitle1" fontWeight="medium">
            {monthNames[month]} {year}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setCurrentDate(new Date(year, month + 1))}
          >
            <KeyboardArrowRight />
          </IconButton>
        </Box>

        <Grid container spacing={0}>
          {dayNames.map((day, index) => (
            <Grid item xs={12 / 7} key={index}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 1,
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                  color: "text.secondary",
                }}
              >
                {day}
              </Box>
            </Grid>
          ))}

          {days.map((date, index) => {
            const isCurrentMonth = date.getMonth() === month;
            const isToday =
              date.toDateString() === currentDateForComparison.toDateString();
            const isSelected = date.getDate() === 14 && isCurrentMonth; // Highlight 14th as shown in design

            return (
              <Grid item xs={12 / 7} key={index}>
                <Box
                  sx={{
                    textAlign: "center",
                    py: 1,
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: isCurrentMonth ? "text.primary" : "text.disabled",
                    backgroundColor: isSelected
                      ? "primary.main"
                      : "transparent",
                    color: isSelected
                      ? "white"
                      : isCurrentMonth
                      ? "text.primary"
                      : "text.disabled",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "primary.dark"
                        : "action.hover",
                    },
                  }}
                >
                  {date.getDate()}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ fontFamily: "Poppins" }}>
      <Header />

      <Box
        sx={{ maxWidth: 1400, mx: "auto", px: 3, marginTop: "100px" }}
      >
        {/* Header */}
        <Typography variant="h4" fontWeight="medium" sx={{ mb: 1 }}>
          Inbox
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Handle new requests, prepare for meetings, and track client progress —
          all from your Inbox.
        </Typography>

        {/* Search and Filter Bar */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            placeholder="Search case summaries, clients, or upcoming dates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
              ),
            }}
            sx={{
              flexGrow: 0.5,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleFilterClick}
            sx={{ minWidth: 120 }}
          >
            Add filter
          </Button>
        </Box>

        {/* Active Filters */}
        {Object.keys(selectedFilters).length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {Object.entries(selectedFilters).map(([filter, value]) => (
              <Chip
                key={filter}
                label={`${filter}: ${value}`}
                onDelete={() => clearFilter(filter)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Box sx={{ display: "flex", gap: 3 }}>
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                sx={{
                  textTransform: "none",
                  color: activeTab === tab.id ? "black" : "#333333",
                  fontFamily: "Poppins",
                  borderBottom: activeTab === tab.id ? 2 : 0,
                  borderColor: "black",
                  borderRadius: 0,
                  fontSize: "16px",
                  px: 2,
                  py: 1.5,
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "black",
                  },
                }}
              >
                {tab.label} ({tab.count})
              </Button>
            ))}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={showOnlyUpdates}
                onChange={(e) => setShowOnlyUpdates(e.target.checked)}
                size="small"
              />
              <Typography variant="body2">
                Show only items with updates
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Show only items with updates checkbox */}

        <Box sx={{ mb: 6 }}>{renderActiveView()}</Box>

        {/* Bottom Action Text */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Want to prepare while you wait?{" "}
            <Button
              variant="text"
              size="small"
              sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
            >
              Set your availability
            </Button>
            , or{" "}
            <Button
              variant="text"
              size="small"
              sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
            >
              Customize your intake form
            </Button>
            .
          </Typography>
        </Box>

        {/* Help Link */}
        <Box sx={{ mt: 6 }}>
          <Button
            variant="text"
            color="primary"
            sx={{ textTransform: "none", p: 0 }}
          >
            Need help?
          </Button>
        </Box>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: { minWidth: 200 },
          }}
        >
          {filterOptions.map((option) => (
            <MenuItem
              key={option}
              onClick={() => {
                if (option === "Appointment Date") {
                  handleCalendarClick();
                } else {
                  toggleFilter(option);
                }
                handleFilterClose();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>

        {/* Calendar Popover */}
        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={handleCalendarClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box>
            {/* Time Range Options */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Past Appointments
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {timeRangeOptions.map((option) => (
                  <Button
                    key={option}
                    variant="text"
                    size="small"
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      color: "text.primary",
                      fontWeight: 400,
                    }}
                    onClick={() => {
                      setSelectedFilters((prev) => ({
                        ...prev,
                        "Appointment Date": option,
                      }));
                      handleCalendarClose();
                    }}
                  >
                    {option}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Calendar */}
            {renderCalendar()}
          </Box>
        </Popover>
      </Box>
    </Box>
  );
}
